/**
 * CSV ETL (Extract, Transform, Load) Service
 * 
 * Handles CSV parsing, column mapping, data normalization, and validation
 * against policy pack entity schemas for government opportunity data.
 */

import Papa from 'papaparse';

// CSV Column mapping configuration
export interface ColumnMapping {
  csvColumn: string;
  entityField: string;
  transform?: (value: any) => any;
  required?: boolean;
}

// Data source templates for common platforms
export interface DataSourceTemplate {
  id: string;
  name: string;
  description: string;
  defaultMappings: ColumnMapping[];
  dateFormat?: string;
  currencySymbol?: string;
  country?: string;
}

// ETL Result
export interface ETLResult {
  success: boolean;
  data: any[];
  errors: ETLError[];
  warnings: string[];
  stats: {
    totalRows: number;
    validRows: number;
    invalidRows: number;
    fieldsMatched: number;
    fieldsUnmatched: string[];
  };
}

export interface ETLError {
  row: number;
  field: string;
  value: any;
  message: string;
  severity: 'error' | 'warning';
}

// Pre-defined templates for common data sources
export const DATA_SOURCE_TEMPLATES: Record<string, DataSourceTemplate> = {
  merx: {
    id: 'merx',
    name: 'Merx',
    description: 'Canadian government procurement portal',
    country: 'Canada',
    currencySymbol: 'CAD',
    dateFormat: 'YYYY-MM-DD',
    defaultMappings: [
      { csvColumn: 'Title', entityField: 'title', required: true },
      { csvColumn: 'Solicitation Number', entityField: 'solicitation_number' },
      { csvColumn: 'Organization', entityField: 'organization', required: true },
      { csvColumn: 'Description', entityField: 'description' },
      { csvColumn: 'Posted Date', entityField: 'posted_date', transform: parseDate },
      { csvColumn: 'Closing Date', entityField: 'deadline', transform: parseDate, required: true },
      { csvColumn: 'Status', entityField: 'status', transform: normalizeStatus },
      { csvColumn: 'Location', entityField: 'location' },
      { csvColumn: 'Province', entityField: 'state_province' },
      { csvColumn: 'Value', entityField: 'estimated_value', transform: parseCurrency },
      { csvColumn: 'URL', entityField: 'url' },
      { csvColumn: 'Category', entityField: 'service_categories', transform: (v) => v ? [v] : [] },
    ],
  },
  
  bc_bid: {
    id: 'bc_bid',
    name: 'BC Bid',
    description: 'British Columbia government procurement',
    country: 'Canada',
    currencySymbol: 'CAD',
    dateFormat: 'MM/DD/YYYY',
    defaultMappings: [
      { csvColumn: 'Opportunity Title', entityField: 'title', required: true },
      { csvColumn: 'Reference Number', entityField: 'reference_number' },
      { csvColumn: 'Buyer Organization', entityField: 'organization', required: true },
      { csvColumn: 'Description', entityField: 'description' },
      { csvColumn: 'Posted', entityField: 'posted_date', transform: parseDate },
      { csvColumn: 'Closing Time', entityField: 'deadline', transform: parseDate, required: true },
      { csvColumn: 'Status', entityField: 'status', transform: normalizeStatus },
      { csvColumn: 'Location', entityField: 'location' },
      { csvColumn: 'Estimated Value', entityField: 'estimated_value', transform: parseCurrency },
      { csvColumn: 'Link', entityField: 'url' },
    ],
  },
  
  sap_ariba: {
    id: 'sap_ariba',
    name: 'SAP Ariba',
    description: 'SAP Ariba procurement platform',
    country: 'International',
    dateFormat: 'YYYY-MM-DD',
    defaultMappings: [
      { csvColumn: 'Event Title', entityField: 'title', required: true },
      { csvColumn: 'Event ID', entityField: 'external_id' },
      { csvColumn: 'Organization', entityField: 'organization', required: true },
      { csvColumn: 'Description', entityField: 'description' },
      { csvColumn: 'Published Date', entityField: 'published_date', transform: parseDate },
      { csvColumn: 'Due Date', entityField: 'deadline', transform: parseDate, required: true },
      { csvColumn: 'Status', entityField: 'status', transform: normalizeStatus },
      { csvColumn: 'Region', entityField: 'region' },
      { csvColumn: 'Country', entityField: 'country' },
      { csvColumn: 'Estimated Value', entityField: 'estimated_value', transform: parseCurrency },
      { csvColumn: 'Currency', entityField: 'currency' },
      { csvColumn: 'URL', entityField: 'url' },
      { csvColumn: 'Commodity Codes', entityField: 'commodity_codes', transform: (v) => v ? v.split(',').map((s: string) => s.trim()) : [] },
    ],
  },
  
  jaggaer: {
    id: 'jaggaer',
    name: 'JAGGAER',
    description: 'JAGGAER procurement platform',
    country: 'International',
    dateFormat: 'MM/DD/YYYY',
    defaultMappings: [
      { csvColumn: 'Opportunity Name', entityField: 'title', required: true },
      { csvColumn: 'Opportunity ID', entityField: 'external_id' },
      { csvColumn: 'Issuing Organization', entityField: 'organization', required: true },
      { csvColumn: 'Description', entityField: 'description' },
      { csvColumn: 'Issue Date', entityField: 'issue_date', transform: parseDate },
      { csvColumn: 'Response Deadline', entityField: 'deadline', transform: parseDate, required: true },
      { csvColumn: 'Status', entityField: 'status', transform: normalizeStatus },
      { csvColumn: 'Place of Performance', entityField: 'place_of_performance' },
      { csvColumn: 'Estimated Budget', entityField: 'estimated_budget', transform: parseCurrency },
      { csvColumn: 'Link', entityField: 'url' },
    ],
  },
  
  samgov: {
    id: 'samgov',
    name: 'SAM.gov',
    description: 'US federal government procurement (SAM.gov API export)',
    country: 'United States',
    currencySymbol: 'USD',
    dateFormat: 'YYYY-MM-DD',
    defaultMappings: [
      { csvColumn: 'title', entityField: 'title', required: true },
      { csvColumn: 'solicitationNumber', entityField: 'solicitation_number' },
      { csvColumn: 'department', entityField: 'department' },
      { csvColumn: 'subtier', entityField: 'sub_agency' },
      { csvColumn: 'office', entityField: 'office' },
      { csvColumn: 'postedDate', entityField: 'posted_date', transform: parseDate },
      { csvColumn: 'responseDeadLine', entityField: 'deadline', transform: parseDate, required: true },
      { csvColumn: 'naicsCode', entityField: 'naics_codes', transform: (v) => v ? [v] : [] },
      { csvColumn: 'classificationCode', entityField: 'psc_codes', transform: (v) => v ? [v] : [] },
      { csvColumn: 'active', entityField: 'status', transform: (v) => v === 'Yes' ? 'open' : 'closed' },
      { csvColumn: 'award.amount', entityField: 'estimated_value', transform: parseCurrency },
      { csvColumn: 'uiLink', entityField: 'url' },
      { csvColumn: 'description', entityField: 'description' },
      { csvColumn: 'placeOfPerformance.city', entityField: 'city' },
      { csvColumn: 'placeOfPerformance.state', entityField: 'state_province' },
      { csvColumn: 'placeOfPerformance.country', entityField: 'country' },
      { csvColumn: 'pointOfContact.fullName', entityField: 'primary_contact_name' },
      { csvColumn: 'pointOfContact.email', entityField: 'primary_contact_email' },
      { csvColumn: 'pointOfContact.phone', entityField: 'primary_contact_phone' },
    ],
  },
  
  generic: {
    id: 'generic',
    name: 'Generic CSV',
    description: 'Custom CSV format - requires manual column mapping',
    defaultMappings: [],
  },
};

// Transform functions
function parseDate(value: any): string | null {
  if (!value) return null;
  
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return null;
    return date.toISOString();
  } catch {
    return null;
  }
}

function parseCurrency(value: any): number | null {
  if (!value) return null;
  
  // Remove currency symbols, commas, spaces
  const cleaned = String(value).replace(/[$€£¥CAD USD,\s]/gi, '').trim();
  const parsed = parseFloat(cleaned);
  
  return isNaN(parsed) ? null : parsed;
}

function normalizeStatus(value: any): string {
  if (!value) return 'unknown';
  
  const lower = String(value).toLowerCase().trim();
  
  // Map various status formats to standard values
  const statusMap: Record<string, string> = {
    'open': 'open',
    'active': 'open',
    'posted': 'open',
    'published': 'open',
    'closed': 'closed',
    'expired': 'closed',
    'cancelled': 'cancelled',
    'canceled': 'cancelled',
    'awarded': 'awarded',
    'completed': 'awarded',
  };
  
  return statusMap[lower] || 'unknown';
}

/**
 * CSV ETL Service
 */
export class CSVETLService {
  /**
   * Parse CSV file
   */
  async parseCSV(file: File): Promise<{ data: any[]; columns: string[]; errors: any[] }> {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header: string) => header.trim(),
        complete: (results) => {
          const columns = results.meta.fields || [];
          resolve({
            data: results.data,
            columns,
            errors: results.errors,
          });
        },
        error: (error) => {
          reject(new Error(`CSV parsing failed: ${error.message}`));
        },
      });
    });
  }
  
  /**
   * Auto-detect data source template based on CSV columns
   */
  detectTemplate(columns: string[]): DataSourceTemplate | null {
    const columnSet = new Set(columns.map(c => c.toLowerCase()));
    
    // Check each template's mappings
    for (const template of Object.values(DATA_SOURCE_TEMPLATES)) {
      if (template.id === 'generic') continue;
      
      const mappedColumns = template.defaultMappings.map(m => m.csvColumn.toLowerCase());
      const matchCount = mappedColumns.filter(col => columnSet.has(col)).length;
      const matchRatio = matchCount / mappedColumns.length;
      
      // If 60%+ of template columns are present, it's likely this template
      if (matchRatio >= 0.6) {
        return template;
      }
    }
    
    return null;
  }
  
  /**
   * Transform data using column mappings
   */
  async transformData(
    data: any[],
    mappings: ColumnMapping[],
    entitySchema: any,
    options: {
      strict?: boolean;
      skipInvalidRows?: boolean;
      sourceSystem?: string;
    } = {}
  ): Promise<ETLResult> {
    const errors: ETLError[] = [];
    const warnings: string[] = [];
    const validData: any[] = [];
    const unmatchedFields = new Set<string>();
    
    // Get required fields from schema (if available)
    const requiredFields = this.extractRequiredFields(entitySchema);
    
    for (let rowIndex = 0; rowIndex < data.length; rowIndex++) {
      const row = data[rowIndex];
      const transformedRow: any = {
        // Add metadata
        source: options.sourceSystem || 'csv_upload',
        source_system: options.sourceSystem || 'csv_upload',
        data_source: options.sourceSystem || 'csv_upload',
        integration_type: options.sourceSystem || 'csv_upload',
        normalized: true,
        raw_data: { ...row }, // Preserve original data
        opportunity_type: 'RFP', // Default
        country: 'Canada', // Default - can be overridden by mapping
        currency: 'CAD', // Default - can be overridden by mapping
      };
      
      let hasErrors = false;
      
      // Apply mappings
      for (const mapping of mappings) {
        const value = row[mapping.csvColumn];
        
        try {
          // Apply transformation if defined
          const transformedValue = mapping.transform 
            ? mapping.transform(value) 
            : value;
          
          transformedRow[mapping.entityField] = transformedValue;
          
          // Check required fields
          if (mapping.required && (transformedValue === null || transformedValue === undefined || transformedValue === '')) {
            errors.push({
              row: rowIndex + 2, // +2 because row 1 is header
              field: mapping.entityField,
              value,
              message: `Required field '${mapping.csvColumn}' is missing or empty`,
              severity: 'error',
            });
            hasErrors = true;
          }
        } catch (error: any) {
          errors.push({
            row: rowIndex + 2,
            field: mapping.entityField,
            value,
            message: `Transformation failed: ${error.message}`,
            severity: 'error',
          });
          hasErrors = true;
        }
      }
      
      // Check for required fields from entity schema
      for (const reqField of requiredFields) {
        if (!transformedRow[reqField] || transformedRow[reqField] === '') {
          errors.push({
            row: rowIndex + 2,
            field: reqField,
            value: null,
            message: `Required entity field '${reqField}' is not mapped or has no value`,
            severity: options.strict ? 'error' : 'warning',
          });
          
          if (options.strict) {
            hasErrors = true;
          } else {
            warnings.push(`Row ${rowIndex + 2}: Missing required field '${reqField}'`);
          }
        }
      }
      
      // Add row if valid or if we're not skipping invalid rows
      if (!hasErrors || !options.skipInvalidRows) {
        validData.push(transformedRow);
      }
      
      // Track unmatched fields
      for (const key of Object.keys(row)) {
        const isMapped = mappings.some(m => m.csvColumn === key);
        if (!isMapped) {
          unmatchedFields.add(key);
        }
      }
    }
    
    // Add warnings for unmatched fields
    if (unmatchedFields.size > 0) {
      warnings.push(`Unmatched CSV columns (not imported): ${Array.from(unmatchedFields).join(', ')}`);
    }
    
    return {
      success: errors.filter(e => e.severity === 'error').length === 0,
      data: validData,
      errors,
      warnings,
      stats: {
        totalRows: data.length,
        validRows: validData.length,
        invalidRows: data.length - validData.length,
        fieldsMatched: mappings.length,
        fieldsUnmatched: Array.from(unmatchedFields),
      },
    };
  }
  
  /**
   * Extract required fields from entity schema
   */
  private extractRequiredFields(entitySchema: any): string[] {
    if (!entitySchema) return ['title', 'organization', 'deadline']; // Defaults
    
    const requiredFields: string[] = [];
    
    // Extract from schema definition
    if (entitySchema.fields) {
      for (const [fieldName, fieldDef] of Object.entries(entitySchema.fields as Record<string, any>)) {
        if (fieldDef.required === true) {
          requiredFields.push(fieldName);
        }
      }
    }
    
    return requiredFields.length > 0 ? requiredFields : ['title', 'organization', 'deadline'];
  }
  
  /**
   * Validate transformed data against entity schema
   */
  validateData(data: any[], entitySchema: any): { valid: boolean; errors: ETLError[] } {
    const errors: ETLError[] = [];
    
    // TODO: Implement schema validation using policy pack entity definitions
    // For now, just check for critical fields
    const criticalFields = ['title', 'organization'];
    
    data.forEach((row, index) => {
      for (const field of criticalFields) {
        if (!row[field]) {
          errors.push({
            row: index + 2,
            field,
            value: null,
            message: `Critical field '${field}' is missing`,
            severity: 'error',
          });
        }
      }
    });
    
    return {
      valid: errors.length === 0,
      errors,
    };
  }
  
  /**
   * Generate entity ID for opportunity
   */
  generateOpportunityId(row: any): string {
    // Use external ID if available, otherwise generate from key fields
    if (row.external_id) {
      return `${row.source_system}_${row.external_id}`;
    }
    
    if (row.solicitation_number) {
      return `${row.source_system}_${row.solicitation_number}`;
    }
    
    // Fallback: hash of title + organization + deadline
    const key = `${row.title}_${row.organization}_${row.deadline}`;
    return `${row.source_system}_${this.simpleHash(key)}`;
  }
  
  private simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36);
  }
}

export const csvETLService = new CSVETLService();

